import { useMemo } from 'react';

import { CampaignAmountRaised } from '#crowdfunding/components/InfoIcons/CampaignAmountRaised';
import { CampaignCountdown } from '#crowdfunding/components/InfoIcons/CampaignCountdown';
import { CampaignParticipants } from '#crowdfunding/components/InfoIcons/CampaignParticipants';
import { CampaignStoreItem, useShowConsumerCampaignById } from '@givebacks/api/store';
import { Flex, SelectableCard } from 'ui';

type CampaignCardProps = {
  campaign_id?: CampaignStoreItem['uuid'];
  causeId?: string;
  onClick?: () => void;
  isColumn?: boolean;
  campaignData?: Partial<CampaignStoreItem>;
  hideAmountRaised?: boolean;
};

const CampaignDescription = ({
  donations,
  expires_at,
  isColumn,
}: {
  donations?: number;
  expires_at?: string;
  isColumn?: boolean;
}) => {
  return (
    <Flex gap={{ base: 'xs', sm: 'md' }} wrap="wrap" m={isColumn ? 'auto' : undefined}>
      <CampaignParticipants number={donations} />
      <CampaignCountdown end={expires_at} />
    </Flex>
  );
};

const CampaignCard = ({
  campaign_id,
  onClick,
  causeId,
  isColumn = false,
  campaignData = {},
  hideAmountRaised = false,
}: CampaignCardProps) => {
  const { data: _campaign } = useShowConsumerCampaignById({
    campaignId: campaign_id,
    causeId,
  });

  const campaign = {
    ..._campaign,
    ...campaignData,
  };

  const isRallyUp = campaign?.__type === 'rallyup';

  const detail = useMemo(() => {
    if (isRallyUp || hideAmountRaised || !campaign?.show_amount_raised) {
      return undefined;
    }

    return <CampaignAmountRaised amount={campaign?.amount_raised} />;
  }, [isRallyUp, campaign?.amount_raised, hideAmountRaised, campaign?.show_amount_raised]);

  // Return null if there's no meaningful campaign data
  if (!_campaign && (!campaignData || Object.keys(campaignData).length === 0)) {
    return null;
  }

  return (
    <SelectableCard
      h="100%"
      imageSrc={campaign.images?.[0]?.url}
      thumbnailSrc={campaign.thumbnail?.url}
      title={campaign.name || ''}
      isColumn={isColumn}
      description={
        <CampaignDescription
          donations={campaign.number_of_donations}
          expires_at={campaign.expires_at}
          isColumn={isColumn}
        />
      }
      detail={detail}
      onClick={onClick}
    />
  );
};
export { CampaignCard };
